import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Button, Icon, Tooltip } from 'antd'

import LangContext from 'modules/shared/context/langContext'
import actions from '../actions'
import { formatDuration } from '../lib/helpers'
import { voiceText } from './labels'

// Elapsed time since `since`. Mounted only while a call is connected, so no
// interval runs without a call, and it is re-created for every call.
class CallTimer extends Component {
  state = { now: Date.now() }

  componentDidMount() {
    this.timer = setInterval(() => this.setState({ now: Date.now() }), 1000)
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  render() {
    const { since } = this.props
    return formatDuration((this.state.now - since) / 1000)
  }
}

class CallCard extends Component {
  static contextType = LangContext

  state = { collapsed: false }

  render() {
    const i18n = this.context
    const t = key => voiceText(i18n, key)
    const { call, mute, hangup, toggleMute } = this.props
    const connected = call.state === 'active'
    const status = connected ? t('voiceInCall') : t('voiceDialing')
    const title = call.code || call.sipUser || call.imei || '-'

    if (this.state.collapsed) {
      return (
        <Tooltip title={t('voiceExpand')} placement="left">
          <button
            type="button"
            style={collapsedStyle}
            onClick={() => this.setState({ collapsed: false })}
            aria-label={`${status} ${title}`}
          >
            <Icon type="phone" theme="filled" style={{ fontSize: 16 }} />
            <span style={{ marginLeft: 8 }}>
              {connected ? <CallTimer since={call.answeredAt} /> : status}
            </span>
          </button>
        </Tooltip>
      )
    }

    return (
      <div style={cardStyle} role="region" aria-label={t('voiceActiveCall')}>
        <div style={headerStyle}>
          <span aria-live="polite">{status}</span>
          <Tooltip title={t('voiceMinimize')}>
            <Button
              type="link"
              size="small"
              icon="minus"
              aria-label={t('voiceMinimize')}
              onClick={() => this.setState({ collapsed: true })}
              style={{ color: '#fff' }}
            />
          </Tooltip>
        </div>

        <div style={bodyStyle}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>{call.plate || '-'}</div>
          {connected && (
            <div style={timerStyle}>
              <CallTimer since={call.answeredAt} />
            </div>
          )}
        </div>

        <div style={controlsStyle}>
          <div style={controlStyle}>
            <Button
              shape="circle"
              size="large"
              icon="audio"
              onClick={toggleMute}
              disabled={!connected || call.hangingUp}
              aria-pressed={mute}
              aria-label={mute ? t('voiceUnmute') : t('voiceMute')}
              style={circleButtonStyle(mute)}
            />
            <span style={controlLabelStyle}>
              {mute ? t('voiceUnmute') : t('voiceMute')}
            </span>
          </div>
          <div style={controlStyle}>
            <Button
              shape="circle"
              size="large"
              icon="phone"
              onClick={hangup}
              disabled={call.hangingUp}
              aria-label={t('voiceHangup')}
              style={hangupButtonStyle}
            />
            <span style={controlLabelStyle}>{t('voiceHangup')}</span>
          </div>
        </div>
      </div>
    )
  }
}

// Floating card for the current call, fixed bottom-right. Lives in
// VoicePanel, so it stays up across route changes.
function ActiveCallCard({ activeCall, mute, hangup, toggleMute }) {
  if (!activeCall) return null
  // key: every call starts with fresh local state (collapsed, timer).
  return (
    <CallCard
      key={activeCall.id}
      call={activeCall}
      mute={mute}
      hangup={hangup}
      toggleMute={toggleMute}
    />
  )
}

const cardStyle = {
  position: 'fixed',
  right: 24,
  bottom: 24,
  width: 280,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
  color: '#fff',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
  zIndex: 1050,
  overflow: 'hidden',
}

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 8px 6px 12px',
  background: 'rgba(0, 0, 0, 0.1)',
  fontSize: 13,
}

const bodyStyle = {
  padding: '14px 16px 8px',
}

const timerStyle = {
  fontSize: 22,
  marginTop: 6,
  fontVariantNumeric: 'tabular-nums',
}

const controlsStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '8px 16px 14px',
}

const controlStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '0 16px',
}

const controlLabelStyle = {
  fontSize: 12,
  marginTop: 4,
}

const circleButtonStyle = pressed => ({
  border: 'none',
  color: pressed ? '#096dd9' : '#fff',
  background: pressed ? '#fff' : 'rgba(255, 255, 255, 0.2)',
})

const hangupButtonStyle = {
  border: 'none',
  color: '#fff',
  background: '#ff4d4f',
  transform: 'rotate(135deg)',
}

const collapsedStyle = {
  position: 'fixed',
  right: 24,
  bottom: 24,
  display: 'flex',
  alignItems: 'center',
  padding: '8px 14px',
  border: 'none',
  borderRadius: 24,
  background: '#1890ff',
  color: '#fff',
  fontSize: 13,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
  cursor: 'pointer',
  zIndex: 1050,
}

const mapStateToProps = state => ({
  activeCall: state.voice.activeCall,
  mute: state.voice.mute,
})

const mapDispatchToProps = {
  hangup: actions.hangup,
  toggleMute: actions.toggleMute,
}

export default connect(mapStateToProps, mapDispatchToProps)(ActiveCallCard)
